import { useState } from "react";
import { faro } from "@grafana/faro-web-sdk";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type { CatalogueCostRefreshRun } from "@shared-core/data/catalogue-types";

/**
 * The backend's 409 guardrail detail is written for a log, not a user
 * ("…exceeds the pre-flight guardrail of 50; pass confirm=True to
 * proceed.") — designed copy in the dialog, `err.detail` kept only for
 * Faro (coordinator review round 6). The two counts (how many prices this
 * refresh would touch, and the guardrail threshold) are the only two
 * numbers in the backend's message; a shape change there degrades to a
 * generic but still-designed sentence rather than ever showing raw text.
 */
function guardrailConfirmMessage(detail: string): string {
  // Standalone digit runs only — a dictionary/catalogue item id like
  // "dict-a5-flyer" embeds a digit that isn't one of the two counts this is
  // looking for, so a number touching a letter on either side is excluded.
  const numbers = detail.match(/(?<![a-zA-Z])\d+(?![a-zA-Z])/g);
  if (!numbers || numbers.length < 2) {
    return "This refresh covers more prices than usual. Refresh anyway?";
  }
  const [requested, threshold] = numbers;
  return `This will ask Tharstern for ${requested} prices at once, above the ${threshold} you would normally confirm. Refresh anyway?`;
}

interface RefreshScope {
  variantId?: string;
  quantity?: number;
  confirm?: boolean;
}

interface PendingGuardrail {
  key: string;
  scope: RefreshScope;
  message: string;
}

/** Defaults kept as the previous hard-coded values; injectable so tests need no real clock. */
export interface PollOptions {
  attempts?: number;
  intervalMs?: number;
}

/**
 * The run either reached a terminal state, or we stopped waiting for it.
 *
 * `timed_out` is deliberately distinct from a finished run: the work may well
 * still be going, and saying either "refreshed" or "failed" about it would be
 * a guess. It is the caller's job to say so rather than pick one.
 */
type PollOutcome =
  { kind: "finished"; run: CatalogueCostRefreshRun } | { kind: "timed_out" };

async function waitForRunToFinish(
  getRun: (runId: string) => Promise<CatalogueCostRefreshRun>,
  runId: string,
  { attempts = 30, intervalMs = 1000 }: PollOptions = {},
): Promise<PollOutcome> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    const run = await getRun(runId);
    // Return the run, not just the fact that it stopped. Answering "has it
    // stopped?" instead of "how did it end?" is what let a run that failed
    // every single pair report a green "Cost refreshed".
    if (run.status !== "RUNNING") return { kind: "finished", run };
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return { kind: "timed_out" };
}

/**
 * Shared cost-refresh trigger flow for VariantCostMatrix and
 * CostFreshnessIndicator: triggers a refresh, polls the run to completion,
 * and reloads on success. When the backend's pre-flight pair-count
 * guardrail rejects the request (409), surfaces `pendingGuardrail` instead
 * of failing outright — the caller renders a confirm dialog and calls
 * `confirmGuardrail()` to retry with `confirm: true`.
 */
export function useVariantCostRefresh(
  itemId: string,
  onDone: () => void,
  pollOptions?: PollOptions,
) {
  const adapter = useDataAdapter();
  const [refreshingKey, setRefreshingKey] = useState<string | null>(null);
  const [pendingGuardrail, setPendingGuardrail] =
    useState<PendingGuardrail | null>(null);

  const run = async (key: string, scope: RefreshScope) => {
    setRefreshingKey(key);
    try {
      const { runId } = await adapter.triggerCatalogueCostRefresh(
        itemId,
        scope,
      );
      const outcome = await waitForRunToFinish(
        (id) => adapter.getCatalogueCostRefreshRun(id),
        runId,
        pollOptions,
      );

      // Reload regardless of outcome: a partial run has already written the
      // pairs that did succeed, and a failed pair still updates its row's
      // status and error reason. The screen should show whatever actually
      // happened — which is the point of the reporting below.
      onDone();

      if (outcome.kind === "timed_out") {
        toast.warning("Still refreshing — this is taking longer than usual", {
          description:
            "The run has not finished yet. Reopen the item shortly to see the result.",
        });
        return;
      }

      const { run: finished } = outcome;
      if (finished.status === "FAILED") {
        // Setup-level failure: nothing was priced at all.
        toast.error("Cost refresh failed", {
          description: finished.failureReason ?? undefined,
        });
        return;
      }
      if (finished.failedCount > 0) {
        // COMPLETE with failures is a normal terminal state, including the
        // all-failed case. Reporting it as success is the defect this fixes.
        const allFailed = finished.succeededCount === 0;
        toast.warning(
          allFailed
            ? `Cost refresh failed for all ${finished.failedCount} price${finished.failedCount === 1 ? "" : "s"}`
            : `Refreshed ${finished.succeededCount} of ${finished.totalPairs} prices`,
          {
            description: `${finished.failedCount} could not be priced — see the affected rows for why.`,
          },
        );
        return;
      }
      toast.success(
        finished.succeededCount > 0
          ? `Cost refreshed (${finished.succeededCount} price${finished.succeededCount === 1 ? "" : "s"})`
          : "Cost refreshed",
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        faro.api?.pushError(
          new Error("catalogue.cost_refresh.guardrail_triggered"),
          {
            context: { itemId, detail: err.detail },
          },
        );
        setPendingGuardrail({
          key,
          scope,
          message: guardrailConfirmMessage(err.detail),
        });
        return;
      }
      toast.error("Cost refresh failed");
    } finally {
      setRefreshingKey(null);
    }
  };

  const confirmGuardrail = () => {
    if (!pendingGuardrail) return;
    const { key, scope } = pendingGuardrail;
    setPendingGuardrail(null);
    void run(key, { ...scope, confirm: true });
  };

  return {
    refreshingKey,
    requestRefresh: run,
    pendingGuardrail,
    confirmGuardrail,
    cancelGuardrail: () => setPendingGuardrail(null),
  };
}
